
import { useState } from 'react';

const sections = [
  {
    id: 'origin',
    title: 'Why I Started Vazcular.com: A Story Written in My Blood',
    content: [
      'My name is Jayashekar Logu, and two years ago, my life changed in a way I never expected.',
      'I was 43, a Product Manager at Cisco Systems in San Jose, living what I believed was a normal, healthy life. Until the day my normal suddenly broke. A routine check turned into an alarming discovery, and within weeks I was lying on an operating table at Stanford Palo Alto, undergoing a quadruple heart bypass surgery.',
      'But the truth is, my story didn\'t begin there. It began decades earlier\u2014with my family. Heart disease runs in my bloodline with a quiet, devastating persistence. Many of my uncles and aunts passed away before the age of 58. I grew up hearing stories of sudden losses, unexplained chest pain, \u201cbad cholesterol,\u201d and heart attacks that arrived too early and too often.',
      'What I didn\'t know then\u2014but painfully understand now\u2014is that genetics doesn\'t whisper; it warns. And sometimes, we don\'t hear it until it\'s too late.',
    ],
  },
  {
    id: 'second-chance',
    title: 'A Second Chance\u2014and a New Purpose',
    content: [
      'Waking up after surgery, surrounded by machines and uncertainty, I felt something shift inside me. Survival had given me a second chance, but it also handed me a responsibility.',
      'At 45 today, I am profoundly grateful to simply be alive.',
      'In the two years since my surgery, I\'ve immersed myself in everything I could learn about heart disease\u2014its hidden patterns, its slow progression, and most importantly, its preventable risks. This journey of self-education led me to become a Stanford Ambassador to the Family Heart Foundation, a role that reinforced one truth:',
      'When we think of heart disease, we often picture risks related to diet, exercise, and lifestyle. But for millions of people, the greatest risk isn\'t about choices\u2014it\'s written in their DNA from the day they were born. These familial heart diseases are inherited conditions that can silently affect anyone, including the young and seemingly healthy.',
      'Familial Hypercholesterolemia (FH) is a genetic disorder that causes extremely high levels of "bad" (LDL) cholesterol from birth. This isn\'t a condition developed over time; it\'s a lifelong burden on the cardiovascular system. The most surprising fact is its frequency: FH affects approximately 1 in 250 people worldwide. To put that in perspective, it\'s about twice as common as Type 1 diabetes in adults, yet receives far less public attention. The early and persistent buildup of cholesterol can lead to heart attacks or strokes at a young age.',
    ],
  },
  {
    id: 'why-vazcular',
    title: 'Why Vazcular.com Exists',
    content: [
      'That realization is what gave birth to Vazcular.com.',
      'I built this platform with one mission: to help people understand heart disease before it becomes their story. Not after the symptoms. Not after the emergency. But early\u2014guided by family history, genetic awareness, and risk education.',
      'South Asian and Hispanic communities, in particular, face an outsized risk of early and severe heart disease. Yet, these groups remain underserved\u2014culturally, medically, and educationally. I want to change that. Through community outreach, simplified medical education, personal storytelling, and culturally relevant guidance, I hope Vazcular.com becomes a place where people feel seen, informed, and supported.',
    ],
  },
  {
    id: 'genetics',
    title: 'Why Genetics & Family History Matter',
    content: [
      'Because I am living proof that when genetics speaks, you need to listen.',
      'If I had known at 25 what I learned at 43, my journey might have been different. Maybe my arteries wouldn\'t have clogged so young. Maybe surgery wouldn\'t have been necessary. Maybe I could have broken the cycle that has claimed so many in my family.',
      'And that is why I\'m sharing my story now\u2014so others won\'t have to learn the hard way.',
    ],
  },
  {
    id: 'walking',
    title: 'Walking With You on Your Journey',
    content: [
      'As I continue this mission, I want people to know that they do not have to walk their path alone. I am always open to listening\u2014your family history, your struggles, your questions, your fears. While I am not a doctor, my lived experience and years of learning have given me a perspective that I\'ve seen help many others find clarity and direction.',
      'I offer consultations to help guide individuals toward the right questions, the right screenings, and the right conversations with their healthcare providers. But any insights I share should always be followed by a conversation with your doctor, and your doctor\'s medical advice must always lead the way.',
      'My role is simply to stand beside you, shine a light where I can, and help you take the steps that genetics, family history, and early awareness make so important.',
    ],
  },
];

export default function HeartBehind() {
  const [expandedSection, setExpandedSection] = useState<string | null>('origin');

  return (
    <section className="py-24 bg-white relative">
      {/* Subtle background accent */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-[#f04e58]/3 rounded-full blur-3xl" />
      <div className="absolute bottom-0 left-0 w-80 h-80 bg-[#0d9488]/3 rounded-full blur-3xl" />

      <div className="max-w-6xl mx-auto px-6 relative z-10">
        {/* Profile Header */}
        <div className="flex flex-col lg:flex-row items-center gap-10 mb-20">
          <div className="relative flex-shrink-0">
            <div className="w-44 h-44 rounded-full overflow-hidden border-4 border-white shadow-2xl ring-4 ring-[#f04e58]/10">
              <img
                src="https://static.readdy.ai/image/ff0b7e80ce034da2f0ffbed7fce4b50d/a9bc80ffb9ab16210f9cdf03a6aeddc0.jpeg"
                alt="Jayashekar Logu"
                className="w-full h-full object-cover object-top"
              />
            </div>
            <div className="absolute -bottom-2 -right-2 w-12 h-12 bg-[#f04e58] rounded-full flex items-center justify-center shadow-lg">
              <i className="ri-heart-pulse-fill text-white text-lg"></i>
            </div>
          </div>
          <div className="text-center lg:text-left">
            <h2 className="text-4xl md:text-5xl font-semibold text-gray-900 mb-3 tracking-tight">
              Jayashekar Logu
            </h2>
            <div className="flex flex-wrap items-center justify-center lg:justify-start gap-3 mb-4">
              <span className="inline-flex items-center px-4 py-1.5 rounded-full bg-[#f04e58]/10 text-[#f04e58] text-sm font-medium">
                Co-Founder
              </span>
              <span className="inline-flex items-center px-4 py-1.5 rounded-full bg-[#0d9488]/10 text-[#0d9488] text-sm font-medium">
                Stanford Hospitals Ambassador to Familial Heart Foundation
              </span>
            </div>
            <p className="text-gray-500 text-base font-light max-w-lg leading-relaxed">
              Dedicated to helping communities understand and prevent familial heart disease through awareness and education.
            </p>
          </div>
        </div>

        {/* Highlight Quote */}
        <div className="relative bg-gradient-to-r from-[#f04e58] to-[#e8434d] rounded-2xl p-10 mb-20 overflow-hidden shadow-xl">
          <div className="absolute top-0 right-0 w-48 h-48 bg-white/5 rounded-full -mr-16 -mt-16" />
          <div className="absolute bottom-0 left-0 w-32 h-32 bg-white/5 rounded-full -ml-10 -mb-10" />
          <div className="relative z-10 flex items-start gap-6">
            <div className="hidden md:flex w-16 h-16 flex-shrink-0 rounded-full bg-white/20 items-center justify-center">
              <i className="ri-double-quotes-l text-3xl text-white"></i>
            </div>
            <div>
              <p className="text-xl md:text-2xl text-white font-medium leading-relaxed mb-3">
                Family history matters. Genetics matter. Awareness matters.
              </p>
              <p className="text-white/70 text-sm font-light">
                The truth I learned after my quadruple heart bypass at age 43
              </p>
            </div>
          </div>
        </div>

        {/* Story Sections - Accordion Style */}
        <div className="space-y-4">
          {sections.map((section) => {
            const isExpanded = expandedSection === section.id;
            return (
              <div
                key={section.id}
                className={`rounded-2xl border transition-all duration-300 ${
                  isExpanded
                    ? 'border-[#f04e58]/20 bg-gradient-to-br from-[#fef5f5]/50 to-white shadow-lg'
                    : 'border-gray-100 bg-white hover:border-gray-200 hover:shadow-sm'
                }`}
              >
                <button
                  onClick={() => setExpandedSection(isExpanded ? null : section.id)}
                  className="w-full flex items-center justify-between p-6 md:p-8 text-left cursor-pointer group"
                >
                  <h3
                    className={`text-lg md:text-xl font-semibold transition-colors pr-4 ${
                      isExpanded ? 'text-[#f04e58]' : 'text-gray-900 group-hover:text-[#f04e58]'
                    }`}
                  >
                    {section.title}
                  </h3>
                  <div
                    className={`w-10 h-10 flex-shrink-0 rounded-full flex items-center justify-center transition-all ${
                      isExpanded ? 'bg-[#f04e58] rotate-180' : 'bg-gray-100 group-hover:bg-[#f04e58]/10'
                    }`}
                  >
                    <i className={`ri-arrow-down-s-line text-xl ${isExpanded ? 'text-white' : 'text-gray-500'}`}></i>
                  </div>
                </button>
                <div
                  className={`overflow-hidden transition-all duration-500 ease-in-out ${
                    isExpanded ? 'max-h-[2000px] opacity-100' : 'max-h-0 opacity-0'
                  }`}
                >
                  <div className="px-6 md:px-8 pb-8 space-y-5">
                    {section.content.map((paragraph, idx) => (
                      <p key={idx} className="text-gray-600 leading-relaxed text-base font-light">
                        {paragraph}
                      </p>
                    ))}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Mission Promises */}
        <div className="mt-20 text-center">
          <h3 className="text-2xl md:text-3xl font-semibold text-gray-900 mb-4">
            A Mission Written From the Heart
          </h3>
          <p className="text-gray-500 font-light mb-10 max-w-lg mx-auto">
            Vazcular.com is more than a website. It is my promise to my family, to my community, and to everyone carrying silent risk in their genes.
          </p>
          <div className="flex flex-col md:flex-row items-stretch justify-center gap-6 max-w-3xl mx-auto">
            {[
              { icon: 'ri-megaphone-line', text: 'A promise to raise awareness' },
              { icon: 'ri-book-open-line', text: 'A promise to educate' },
              {
                icon: 'ri-heart-add-line',
                text: 'A promise to change the trajectory of heart disease, one family at a time',
              },
            ].map((item, idx) => (
              <div
                key={idx}
                className="flex-1 bg-white rounded-2xl p-6 border border-gray-100 shadow-sm hover:shadow-md hover:border-[#f04e58]/20 transition-all duration-300 group"
              >
                <div className="w-12 h-12 rounded-xl bg-[#f04e58]/10 flex items-center justify-center mx-auto mb-4 group-hover:bg-[#f04e58]/20 transition-colors">
                  <i className={`${item.icon} text-xl text-[#f04e58]`}></i>
                </div>
                <p className="text-sm text-gray-700 font-medium leading-relaxed">{item.text}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
